import { NodeEnvTypes } from '../../types/generalTypes';
import { isFramed } from '../embedded/embeddedService';

// Embed-only guard.
//
// The Received UI is meant to be used only inside the Stigg app's iframe (app.stigg.io /
// app-staging.stigg.io). When the SPA is loaded top-level we send the user to the Stigg app
// instead. This replaces an edge (CloudFront) redirect, so the rule lives here. It is a UX
// guard, not a security boundary — the server enforces access.
//
// Separate from isEmbedded() / `?embeddedPage=true`, which only drives styling.
//
// Not redirected:
// - framed (window.self !== window.top), incl. `/login?token=...` inside the iframe;
// - payer-facing pages under `/cards/` — the Airwallex checkout success/cancel return URLs
//   (server: airwallex-payment.provider.ts success_url / cancel_url) are loaded top-level by the
//   vendor's end customer, who must never be sent to the Stigg dashboard;
// - the `/settings` onboarding return landing of popups opened by our own app (payment-provider
//   onboarding via window.open, which Stripe / Airwallex / OAuth return top-level to `/settings`)
//   — detected by a same-origin opener. Exactly `/settings` (every server return URL is that path),
//   so such a popup tab doesn't become a standing un-embedded copy of the app once the user
//   navigates away from the landing;
// - dev (import.meta.env.DEV) and the `sandbox` NODE_ENV (it has its own public signup/login);
// - STIGG_APP_URL unset/invalid: fail open (with a console warning) so a deploy that lands before
//   the Secrets Manager key is added doesn't lock anyone out.

type GuardWindow = Pick<Window, 'self' | 'top' | 'opener' | 'location' | '_env_'>;

export interface EmbedOnlyGuardOptions {
	isDev: boolean;
	win?: GuardWindow;
}

// Runs once per page load, so there is at most one warning per load.
const warnUrlIssue = (reason: string) => {
	console.warn(`[embedOnlyGuard] ${reason}; skipping the top-level redirect to the Stigg app.`);
};

// A popup opened by our own app (window.open without `noopener`) has a same-origin opener.
// Reading a cross-origin opener's location throws, which means it isn't ours.
// Caveat (COOP): if a provider page in the popup chain sends `Cross-Origin-Opener-Policy:
// same-origin`, the browser severs the opener and the `/settings` landing gets redirected to the
// Stigg app. No data is lost (e.g. the Stripe OAuth code is exchanged server-side), but verify
// each onboarding flow on staging.
const isOpenedBySameOrigin = (win: GuardWindow): boolean => {
	try {
		const opener = win.opener as Window | null;
		if (!opener) return false;
		return opener.location.origin === win.location.origin;
	} catch {
		return false;
	}
};

const PAYER_FACING_PATH_PREFIX = '/cards/';
const ONBOARDING_RETURN_PATH = '/settings';

// Exactly `/settings` (query strings aren't part of pathname): not `/settings/...` or look-alikes.
const isOnboardingReturnPath = (pathname: string): boolean => pathname === ONBOARDING_RETURN_PATH;

const isPayerFacingPath = (win: GuardWindow): boolean => win.location.pathname.startsWith(PAYER_FACING_PATH_PREFIX);

const isOnboardingReturnPopup = (win: GuardWindow): boolean => isOnboardingReturnPath(win.location.pathname) && isOpenedBySameOrigin(win);

const resolveStiggAppUrl = (raw: string | undefined): string | undefined => {
	if (!raw) {
		warnUrlIssue('STIGG_APP_URL is not set');
		return undefined;
	}
	try {
		const url = new URL(raw);
		if (url.protocol !== 'https:' && url.protocol !== 'http:') {
			throw new Error('unsupported protocol');
		}
		return url.href;
	} catch {
		// The value is left out on purpose: FullStory can capture console output.
		warnUrlIssue('STIGG_APP_URL is not a valid http(s) URL');
		return undefined;
	}
};

/**
 * Redirects to the Stigg app (via location.replace, so Back doesn't loop) when the UI is loaded
 * top-level. Returns true when a redirect was triggered — the caller must then skip rendering.
 */
export const enforceEmbedOnly = ({ isDev, win = window }: EmbedOnlyGuardOptions): boolean => {
	if (isDev || win._env_?.NODE_ENV === NodeEnvTypes.SANDBOX) return false;
	if (isFramed(win) || isPayerFacingPath(win) || isOnboardingReturnPopup(win)) return false;

	const target = resolveStiggAppUrl(win._env_?.STIGG_APP_URL);
	if (!target) return false;

	win.location.replace(target);
	return true;
};
